<script setup lang="ts">
// Global celebration overlay — Sprint 1 §11.3 + 2026-06-08 expansion.
//
// Mounted once at the App root. Subscribes to celebrationStore.current and
// renders the active celebration. Auto-dismisses after a timeout proportional
// to asset intensity (1 = 1.5s, 5 = 4s). Reduced-motion users get a
// non-animated static pill.
//
// Render strategy:
//   1. If asset_url present  -> render the GCS asset (img / video).
//   2. Otherwise, pick a built-in style from the payload (or rotate random).
//   3. Built-in styles: confetti, fireworks, balloons, sparkles, stars,
//      hearts, applause, trophy.
//
// Each built-in style is a pure-CSS animation — no JS animation lib, no
// external dep. Keeps bundle size small and respects reduced-motion.

import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import {
  useCelebrationStore,
  prefersReducedMotion,
  pickRandomStyle,
  BUILTIN_STYLES,
  type CelebrationStyle,
} from '@/stores/celebration'

const store = useCelebrationStore()
const visible = ref(false)
const reducedMotion = ref(prefersReducedMotion())
let dismissTimer: ReturnType<typeof setTimeout> | null = null

const current = computed(() => store.current)

const durationMs = computed(() => {
  const intensity = current.value?.intensity ?? 3
  return 1500 + (intensity - 1) * 625
})

const assetKind = computed(() => current.value?.asset_kind ?? null)
const assetUrl = computed(() => current.value?.asset_url ?? null)

const chosenStyle = computed<CelebrationStyle>(() => {
  const requested = current.value?.style
  if (
    requested &&
    (BUILTIN_STYLES as readonly string[]).includes(requested as string)
  ) {
    return requested as CelebrationStyle
  }
  return pickRandomStyle()
})

watch(current, (next) => {
  if (next) {
    visible.value = true
    if (dismissTimer) clearTimeout(dismissTimer)
    dismissTimer = setTimeout(() => {
      visible.value = false
      setTimeout(() => store.dismiss(), 300)
    }, durationMs.value)
  } else {
    visible.value = false
  }
})

function onDismissClick() {
  if (dismissTimer) clearTimeout(dismissTimer)
  visible.value = false
  setTimeout(() => store.dismiss(), 200)
}

onMounted(() => {
  if (window.matchMedia) {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = () => (reducedMotion.value = mq.matches)
    mq.addEventListener?.('change', onChange)
    onUnmounted(() => mq.removeEventListener?.('change', onChange))
  }
})

onUnmounted(() => {
  if (dismissTimer) clearTimeout(dismissTimer)
})

const PARTICLE_COUNTS: Record<CelebrationStyle, number> = {
  confetti: 28,
  fireworks: 24,
  balloons: 12,
  sparkles: 30,
  stars: 16,
  hearts: 14,
  applause: 0,
  trophy: 0,
}
</script>

<template>
  <Transition name="celebration-fade">
    <div
      v-if="visible && current"
      class="celebration-overlay"
      :class="{ 'celebration-overlay--reduced': reducedMotion }"
      role="status"
      aria-live="polite"
      @click="onDismissClick"
    >
      <div class="celebration-overlay__inner">
        <!-- Reduced motion: static pill regardless of style. -->
        <div v-if="reducedMotion" class="celebration-overlay__pill">
          Nice work!
        </div>

        <!-- Asset path: a GCS-backed asset wins over built-in. -->
        <template v-else-if="assetUrl">
          <img
            v-if="assetKind === 'svg' || assetKind === 'gif' || assetKind === 'apng'"
            :src="assetUrl"
            class="celebration-overlay__asset"
            alt=""
            aria-hidden="true"
          />
          <video
            v-else-if="assetKind === 'webm' || assetKind === 'mp4'"
            :src="assetUrl"
            class="celebration-overlay__asset"
            autoplay
            muted
            playsinline
          />
          <img
            v-else
            :src="assetUrl"
            class="celebration-overlay__asset"
            alt=""
            aria-hidden="true"
          />
        </template>

        <!-- Built-in animation registry (no GCS asset). -->
        <template v-else>
          <!-- Confetti -->
          <div
            v-if="chosenStyle === 'confetti'"
            class="celebration-overlay__particles celebration-overlay__particles--confetti"
            aria-hidden="true"
          >
            <span
              v-for="i in PARTICLE_COUNTS.confetti"
              :key="i"
              :style="{ '--i': i, '--n': PARTICLE_COUNTS.confetti }"
            />
          </div>

          <!-- Fireworks: bursts of sparks -->
          <div
            v-else-if="chosenStyle === 'fireworks'"
            class="celebration-overlay__fireworks"
            aria-hidden="true"
          >
            <div v-for="ring in 3" :key="ring" class="firework" :style="{ '--ring': ring }">
              <span
                v-for="i in PARTICLE_COUNTS.fireworks"
                :key="i"
                :style="{ '--i': i, '--n': PARTICLE_COUNTS.fireworks }"
              />
            </div>
          </div>

          <!-- Balloons rising -->
          <div
            v-else-if="chosenStyle === 'balloons'"
            class="celebration-overlay__particles celebration-overlay__particles--balloons"
            aria-hidden="true"
          >
            <span
              v-for="i in PARTICLE_COUNTS.balloons"
              :key="i"
              :style="{ '--i': i, '--n': PARTICLE_COUNTS.balloons }"
            >
              🎈
            </span>
          </div>

          <!-- Sparkles -->
          <div
            v-else-if="chosenStyle === 'sparkles'"
            class="celebration-overlay__particles celebration-overlay__particles--sparkles"
            aria-hidden="true"
          >
            <span
              v-for="i in PARTICLE_COUNTS.sparkles"
              :key="i"
              :style="{ '--i': i, '--n': PARTICLE_COUNTS.sparkles }"
            >
              ✨
            </span>
          </div>

          <!-- Stars: pulsing burst -->
          <div
            v-else-if="chosenStyle === 'stars'"
            class="celebration-overlay__particles celebration-overlay__particles--stars"
            aria-hidden="true"
          >
            <span
              v-for="i in PARTICLE_COUNTS.stars"
              :key="i"
              :style="{ '--i': i, '--n': PARTICLE_COUNTS.stars }"
            >
              ⭐
            </span>
          </div>

          <!-- Hearts rising -->
          <div
            v-else-if="chosenStyle === 'hearts'"
            class="celebration-overlay__particles celebration-overlay__particles--hearts"
            aria-hidden="true"
          >
            <span
              v-for="i in PARTICLE_COUNTS.hearts"
              :key="i"
              :style="{ '--i': i, '--n': PARTICLE_COUNTS.hearts }"
            >
              ❤️
            </span>
          </div>

          <!-- Applause: pulsing text -->
          <div
            v-else-if="chosenStyle === 'applause'"
            class="celebration-overlay__applause"
            aria-hidden="true"
          >
            👏 Nice work!
          </div>

          <!-- Trophy: bouncing trophy -->
          <div
            v-else-if="chosenStyle === 'trophy'"
            class="celebration-overlay__trophy"
            aria-hidden="true"
          >
            🏆
          </div>
        </template>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.celebration-overlay {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  pointer-events: auto;
  background: rgba(10, 14, 26, 0.05);
  z-index: 9000;
}

.celebration-overlay__inner {
  display: grid;
  place-items: center;
  pointer-events: none;
  width: 100%;
  height: 100%;
}

.celebration-overlay__asset {
  max-width: min(70vw, 560px);
  max-height: 70vh;
  filter: drop-shadow(var(--shadow-lg));
}

.celebration-overlay__pill {
  background: var(--band-correct-soft);
  color: var(--band-correct-ink);
  padding: var(--space-sm) var(--space-lg);
  border-radius: var(--radius-pill);
  font-weight: 600;
  box-shadow: var(--shadow-md);
}

/* Particle container shared by confetti / sparkles / stars / hearts / balloons */
.celebration-overlay__particles {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.celebration-overlay__particles span {
  --i: 0;
  --n: 1;
  position: absolute;
}

/* Confetti — rectangles falling from above */
.celebration-overlay__particles--confetti span {
  top: -20px;
  left: calc((var(--i) / var(--n)) * 100%);
  width: 10px;
  height: 14px;
  background: hsl(calc(var(--i) * 19), 80%, 60%);
  animation: confetti-fall 2200ms ease-in forwards;
  animation-delay: calc(var(--i) * 40ms);
  transform-origin: center;
}

@keyframes confetti-fall {
  to { transform: translateY(110vh) rotate(720deg); opacity: 0; }
}

/* Balloons — rising emoji from below */
.celebration-overlay__particles--balloons span {
  bottom: -60px;
  left: calc((var(--i) / var(--n)) * 95% + 2.5%);
  font-size: 2.2rem;
  animation: balloon-rise 3200ms ease-out forwards;
  animation-delay: calc(var(--i) * 120ms);
}

@keyframes balloon-rise {
  0%   { transform: translateY(0) rotate(-5deg); opacity: 1; }
  90%  { opacity: 1; }
  100% { transform: translateY(-110vh) rotate(8deg); opacity: 0; }
}

/* Sparkles — twinkling across the screen */
.celebration-overlay__particles--sparkles span {
  top: calc((sin(var(--i) * 0.7) * 0.5 + 0.5) * 90% + 5%);
  left: calc((var(--i) / var(--n)) * 100%);
  font-size: 1.6rem;
  animation: sparkle-twinkle 1800ms ease-in-out infinite;
  animation-delay: calc(var(--i) * 80ms);
  opacity: 0;
}

@keyframes sparkle-twinkle {
  0%, 100% { opacity: 0; transform: scale(0.5); }
  50%      { opacity: 1; transform: scale(1.2) rotate(180deg); }
}

/* Stars — pulsing burst from center */
.celebration-overlay__particles--stars span {
  top: 50%;
  left: 50%;
  font-size: 2rem;
  --angle: calc(var(--i) * (360deg / var(--n)));
  animation: star-burst 2200ms ease-out forwards;
  animation-delay: calc(var(--i) * 30ms);
}

@keyframes star-burst {
  0%   { transform: translate(-50%, -50%) scale(0.2); opacity: 0; }
  20%  { opacity: 1; }
  100% {
    transform:
      translate(-50%, -50%)
      rotate(var(--angle))
      translateX(40vh)
      rotate(calc(-1 * var(--angle)))
      scale(1.4);
    opacity: 0;
  }
}

/* Hearts — rising and gently swaying */
.celebration-overlay__particles--hearts span {
  bottom: -60px;
  left: calc((var(--i) / var(--n)) * 90% + 5%);
  font-size: 2rem;
  animation: heart-rise 2800ms ease-out forwards;
  animation-delay: calc(var(--i) * 140ms);
}

@keyframes heart-rise {
  0%   { transform: translateY(0) scale(0.6); opacity: 0; }
  20%  { opacity: 1; transform: translateY(-20vh) scale(1); }
  60%  { transform: translateY(-60vh) scale(1.1) translateX(10px); }
  100% { transform: translateY(-110vh) scale(0.8) translateX(-15px); opacity: 0; }
}

/* Fireworks — three concentric rings of sparks */
.celebration-overlay__fireworks {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.firework {
  --ring: 1;
  position: absolute;
  top: calc(20% + var(--ring) * 15%);
  left: calc(15% + var(--ring) * 28%);
}

.firework span {
  --i: 0;
  --n: 1;
  position: absolute;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: hsl(calc(var(--i) * 15 + var(--ring) * 60), 90%, 60%);
  animation: firework-spark 1500ms ease-out forwards;
  animation-delay: calc(var(--ring) * 200ms);
  --angle: calc(var(--i) * (360deg / var(--n)));
}

@keyframes firework-spark {
  0%   { transform: rotate(var(--angle)) translateX(0) scale(1); opacity: 1; }
  100% { transform: rotate(var(--angle)) translateX(18vh) scale(0.2); opacity: 0; }
}

/* Applause text */
.celebration-overlay__applause {
  font-size: 3rem;
  font-weight: 700;
  color: var(--band-correct-ink);
  background: var(--band-correct-soft);
  padding: var(--space-md) var(--space-xl);
  border-radius: var(--radius-pill);
  box-shadow: var(--shadow-lg);
  animation: applause-pulse 600ms ease-in-out infinite alternate;
}

@keyframes applause-pulse {
  from { transform: scale(1); }
  to   { transform: scale(1.08); }
}

/* Trophy */
.celebration-overlay__trophy {
  font-size: 6rem;
  filter: drop-shadow(var(--shadow-lg));
  animation: trophy-bounce 1100ms cubic-bezier(0.5, 0, 0.5, 1.4) forwards;
}

@keyframes trophy-bounce {
  0%   { transform: translateY(-100vh) scale(0.5); opacity: 0; }
  60%  { transform: translateY(20px) scale(1.2); opacity: 1; }
  80%  { transform: translateY(-10px) scale(1); }
  100% { transform: translateY(0) scale(1); }
}

.celebration-fade-enter-active,
.celebration-fade-leave-active {
  transition: opacity 200ms ease;
}

.celebration-fade-enter-from,
.celebration-fade-leave-to {
  opacity: 0;
}
</style>
